<script lang="ts">
  import { Form, FormInput, Validate } from "$shared/lib/validation_next";
  import { withApi } from "$shared/lib/api-parkito";
  import SvelteMarkdown from "svelte-markdown";
  import type {
    Queue,
    QueueAvailability,
    QueueStep,
    QueueTag,
    Tag,
  } from "$apis/parkito";
  import { CardInline, Label, ValCheckbox } from "$components";
  import { Hor, Spacer, Ver } from "$components";
  import { derived, get, type Readable, writable } from "svelte/store";
  import { onDestroy } from "svelte";
  import { formatNumber, getPrice } from "$shared/utils/misc";

  export let context: {
    showResidentalParkingPrices: boolean;
    queue: Queue;
    siteTags: Tag[];
  };
  export let tagsOutput: FormInput<string[]>;
  const { steps, id: queueId } = context.queue;
  const tagsCheckedByDefault = steps.flatMap((s) =>
    s.tags.filter((tag) => tag.checkedByDefault).map((tag) => tag.tagId)
  );
  const tagsToEdit = $tagsOutput.res.unwrapOr([]);
  const initialTags: string[] = tagsToEdit.length > 0
    ? tagsToEdit
    : tagsCheckedByDefault;

  const tagsInAllSteps = steps.flatMap((step) =>
    step.tags.map((st) => st.tagId)
  );

  function getStep(stepId: string): QueueStep | undefined {
    return steps.find((step) => step.id === stepId);
  }

  const tagMap = new Map<string, Tag>(
    context.siteTags.map((tag) => [tag.id, tag]),
  );
  const tagStepMap = new Map<string, QueueStep>(
    steps.flatMap((step) => step.tags.map((tag) => [tag.tagId, step])),
  );
  const checkboxMap = new Map<string, Form.Checkbox<boolean>>(
    tagsInAllSteps.map(
      (
        tagId,
      ) => [
        tagId,
        Form.checkbox(Validate.boolean(), initialTags.includes(tagId)),
      ],
    ),
  );

  const aggregateBoxes = new Form.List<boolean, Form.Checkbox<boolean>>([
    ...checkboxMap.values(),
  ]);
  const selectedTags = derived(aggregateBoxes, (aggregateBoxes) => {
    const boxes = aggregateBoxes.res.unwrapOr([]);
    const selectedTags: string[] = [];
    for (let i = 0; i < boxes.length; ++i) {
      if (boxes[i]) {
        selectedTags.push(tagsInAllSteps[i]);
      }
    }
    return selectedTags;
  });

  tagsOutput.subscribeTo(Form.fromStore(selectedTags).output);

  const availableTags = writable<QueueTag[] | undefined>(undefined);
  const availability = writable<QueueAvailability | undefined>(undefined);
  const lowestPrice = derived(availability, (avail) => {
    if (!avail || !avail.lowestPrice) {
      return undefined;
    }
    return getPrice(avail.lowestPrice.normal, true);
  });
  const highestPrice = derived(availability, (avail) => {
    if (!avail || !avail.highestPrice) {
      return undefined;
    }
    return getPrice(avail.highestPrice.normal, true);
  });

  const updateAvailableTags = async (selectedTags: string[]) => {
    const tags = await withApi((api) => {
      return api.queueGetAvailability(queueId, { selectedTags });
    });

    if (tags.isOk()) {
      availableTags.set(tags.value.tags);
      availability.set(tags.value.availability);
    }
  };

  const selectedTagsUnsub = selectedTags.subscribe(updateAvailableTags);

  const isTagAvailableMap: Readable<Map<string, boolean>> = derived(
    availableTags,
    (tagAvailabilities) =>
      new Map(tagsInAllSteps.map((tagId) => {
        if (tagAvailabilities) {
          const foundTagId = [...tagAvailabilities].findIndex((ta) =>
            ta.tag.id === tagId
          );
          return [tagId, foundTagId >= 0];
        }
        return [tagId, true];
      })),
  );

  onDestroy(() => {
    selectedTagsUnsub();
  });

  // If a tag becomes unavailable then we need to uncheck it.
  isTagAvailableMap.subscribe((isTagAvailableMap) => {
    for (const tag of tagsInAllSteps) {
      const checkbox = checkboxMap.get(tag)!;
      const isChecked = get(checkbox).res.unwrapOr(false);
      const isAvailable = isTagAvailableMap.get(tag)!;
      if (isChecked && !isAvailable) {
        checkbox.input.set(false);
      }
    }
  });
</script>

<Ver gap="m">
  {#each steps as step (step.data.stepNr)}
    <Ver gap="s">
      <SvelteMarkdown source={step.data.description} />
      <Hor>
        {#each step.tags as { tagId } (tagId)}
          {@const tag = tagMap.get(tagId)}
          {@const checkbox = checkboxMap.get(tagId)}
          {#if checkbox}
            <ValCheckbox
              disabled={!$isTagAvailableMap.get(tagId)}
              field={checkbox}
              label={tag?.name ?? "Okänd tagg"}
            ></ValCheckbox>
          {:else}
            <Label
              type="error"
              text="En tagg kunde inte hittas. Kontakta admin."
            />
          {/if}
        {/each}
      </Hor>
    </Ver>
  {/each}
  {#if $availability && $selectedTags.length > 0}
    <CardInline title="Ditt urval">
      <p>
        På anläggningen finns det <strong>totalt {$availability.totalCount}
          platser</strong> med de egenskaperna som du valde.
      </p>
      {#if $highestPrice && $lowestPrice && $lowestPrice < $highestPrice}
        <p>
          med pris från <strong>{formatNumber($lowestPrice)}kr</strong> till
          <strong>{formatNumber($highestPrice)}kr</strong>
        </p>
      {:else if       $highestPrice && $lowestPrice &&
        $lowestPrice === $highestPrice}
        <p>
          De alla kostar <strong>{formatNumber($lowestPrice)}kr</strong>
        </p>
      {:else if $highestPrice}
        <p>
          med pris upp till <strong>{formatNumber($highestPrice)}kr</strong>
        </p>
      {:else if $lowestPrice}
        <p>
          med pris från <strong>{formatNumber($lowestPrice)}kr</strong>
        </p>
      {/if}

      <!-- TODO: uncomment this when backend responds with correct value -->
      <!--
        <p>
          Av dessa är just nu <strong>{$availability.freeCount} lediga</strong>.
        </p>
        -->
    </CardInline>
  {/if}
</Ver>
